import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Share2,
  Clock,
  CheckCircle,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Truck,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  Mail,
  ShoppingCart,
  Package,
  Car,
  Maximize2,
  Loader2,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { clsx } from 'clsx'
import { useListing } from '@/hooks'
import { useAuthStore } from '@/store/useAuthStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { ListingPageSkeleton } from '@/components/common/Skeleton'
import { BidSection, RelatedListings, SafetyTips, ImageLightbox, ListingStatusControls, MakeOfferSection } from '@/components/listings'
import { SellerBidList } from '@/components/listings/SellerBidList'
import { EnquiryForm } from '@/components/listings/EnquiryForm'
import { getUserProfile } from '@/services/api/users'
import { getFinanceCalculatorSettings } from '@/services/api/financeSettings'
import { DEFAULT_FINANCE_SETTINGS, type FinanceCalculatorSettings } from '@/types/financeSettings'
import { useCartActions } from '@/hooks/useCart'
import { Timestamp } from 'firebase/firestore'
import type { CartItem } from '@/types/order'
import type { UserProfile } from '@/types/user'
import { FundraiserSection } from '@/components/fundraiser'
import { StorefrontPreviewCard } from '@/components/storefront/StorefrontPreviewCard'
import { SEO } from '@/components/common/SEO'
import { AdCarousel } from '@/components/ads/AdCarousel'
import { FinanceCalculator } from '@/components/listings/FinanceCalculator'
import { AgentContactStack } from '@/components/listings/AgentContactStack'
import {
  formatPrice,
  formatTimeRemaining,
  formatOdometer,
} from '@/utils/format'
import { getCategoryById } from '@/utils/categories'
import { resolveFinanceVisibility } from '@/utils/finance-visibility'
import {
  isVehicleListing,
  isFundraiserListing,
  isAuctionListing,
  listingAcceptsOffers,
  listingHasBuyNow,
  canBuyOutAuction,
  effectivePaymentOptions,
  type Listing,
  type VehicleListing,
  type FundraiserListing,
} from '@/types/listing'
import { buyOutAuction } from '@/services/api/bids'

export function ListingPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { listing, isLoading, error } = useListing(id)
  const { isAuthenticated, user } = useAuthStore()
  const { openModal, showToast } = useUIStore()
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set())
  const [sellerProfile, setSellerProfile] = useState<UserProfile | null>(null)
  const [financeSettings, setFinanceSettings] = useState<FinanceCalculatorSettings | null>(null)
  const [cartQty, setCartQty] = useState(1)
  const [buyingOut, setBuyingOut] = useState(false)
  const { add: addToCart } = useCartActions()

  // Image navigation hooks — must be before early returns
  const nextImage = useCallback(() => {
    setActiveImageIndex((prev) =>
      prev < (listing?.images?.length || 1) - 1 ? prev + 1 : 0
    )
  }, [listing?.images?.length])

  const prevImage = useCallback(() => {
    setActiveImageIndex((prev) =>
      prev > 0 ? prev - 1 : (listing?.images?.length || 1) - 1
    )
  }, [listing?.images?.length])

  // Keyboard navigation for image gallery
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevImage()
      else if (e.key === 'ArrowRight') nextImage()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [nextImage, prevImage])

  useEffect(() => {
    if (listing?.sellerId) {
      getUserProfile(listing.sellerId).then(setSellerProfile).catch(() => {})
    }
  }, [listing?.sellerId])

  // Fetch admin-managed Good Lending settings (cheap singleton doc — every
  // listing page reads it but Firestore caches client-side after first load).
  useEffect(() => {
    getFinanceCalculatorSettings().then(setFinanceSettings).catch(() => {})
  }, [])

  if (isLoading) {
    return <ListingPageSkeleton />
  }

  if (error || !listing) {
    return (
      <div className="container py-12 text-center">
        <div className="text-6xl mb-4" aria-hidden="true">😕</div>
        <h1 className="text-2xl font-bold mb-2">Listing Not Found</h1>
        <p className="text-slate-500 mb-6">
          This listing may have been removed or doesn&apos;t exist.
        </p>
        <Link to="/browse">
          <Button>Browse Listings</Button>
        </Link>
      </div>
    )
  }

  const category = getCategoryById(listing.category)
  const isAuction = isAuctionListing(listing) || (isFundraiserListing(listing) && (listing as FundraiserListing).fundraiserType === 'auction')
  const isOwner = isAuthenticated && user?.uid === listing.sellerId
  const isBuyNow = !isAuction

  // E-commerce mode: listing has 'product' sale type
  const isProduct = listing.saleType === 'product'
  const isInStock = isProduct && (listing.stock ?? 0) > 0
  const isOutOfStock = isProduct && (listing.stock ?? 0) === 0
  const lowStock = isProduct && (listing.stock ?? 0) > 0 && (listing.stock ?? 0) <= 5

  // Buyer-flow toggles (independent of saleType). Dealership listings never
  // show Make Offer — client decision 2026-06-10 (dealers price firm; their
  // imported listings have no enableOffers field so the legacy fallback was
  // wrongly showing the button).
  const hasBuyNow = listingHasBuyNow(listing)
  const acceptsOffers = listingAcceptsOffers(listing) && sellerProfile?.subscriptionTier !== 'dealership'
  const auctionBuyNowPrice = isAuction
    ? (listing as Listing & { buyNowPrice?: number }).buyNowPrice
    : undefined

  // Buy Now is reachable whenever the seller accepts ANY payment method —
  // Stripe Card (requires active Connect), Bank Transfer, or Cash on Pickup.
  // CheckoutPage handles the payment-method picker per Session 51.
  // Dealerships are enquiry-only: no purchase path ever — buyers contact them.
  const isSellerEnquiryOnly = sellerProfile?.subscriptionTier === 'dealership'
  // General sellers always implicitly accept Cash on Pickup (zero setup), so a
  // listing with empty paymentOptions must not read as "payments not set up".
  const paymentOptions = effectivePaymentOptions(
    listing.paymentOptions,
    sellerProfile?.subscriptionTier,
  )
  const sellerAcceptsAnyPayment =
    !isSellerEnquiryOnly && (
      sellerProfile?.stripeConnectStatus === 'active'
      || paymentOptions.includes('Bank Transfer')
      || paymentOptions.includes('Cash on Pickup')
      || paymentOptions.includes('Cash')
    )

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    if (!listing || !isInStock) return
    addToCart({
      listingId: listing.id,
      sellerId: listing.sellerId,
      price: listing.price ?? 0,
      title: listing.title,
      image: listing.images?.[0] || '',
      quantity: cartQty,
      stock: listing.stock,
    })
    setCartQty(1)
  }

  const handleBuyNow = (priceOverride?: number) => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    const price = priceOverride ?? listing?.price ?? 0
    if (!listing || !price) return
    // Buy Now bypasses the cart: a one-off classified purchase (e.g. a car) should
    // never become a cart line-item. The item is handed to the checkout via nav
    // state, which renders a single-item "Complete your purchase" flow.
    const buyNowItem: CartItem = {
      id: listing.id,
      listingId: listing.id,
      sellerId: listing.sellerId,
      quantity: 1,
      price,
      title: listing.title,
      image: listing.images?.[0] || '',
      addedAt: Timestamp.now(),
    }
    navigate('/checkout', { state: { buyNow: buyNowItem } })
  }

  const handleAuctionBuyOut = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    if (!listing) return
    setBuyingOut(true)
    try {
      const res = await buyOutAuction(listing.id)
      if (!res.orderId) {
        showToast('Something went wrong finalising your order — please contact support.', 'error')
        return
      }
      showToast('Purchase confirmed — complete payment on your order.', 'success')
      navigate(`/orders?orderId=${res.orderId}`)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Buy Now failed. Please try again.'
      showToast(message, 'error')
    } finally {
      setBuyingOut(false)
    }
  }


  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `Check out ${listing.title} on iSQROLL`,
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      showToast('Link copied to clipboard!')
    }
  }

  const handleContact = () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    // Can't message yourself about your own listing.
    if (user?.uid === listing.sellerId) return
    openModal('message', {
      listingId: listing.id,
      sellerId: listing.sellerId,
      sellerName: listing.sellerName,
      listingTitle: listing.title,
      listingImage: listing.images?.[0],
      listingPrice: listing.price ?? undefined,
      listingType: listing.listingType,
    })
  }

  return (
    <div className="pb-36 md:pb-8">
      <SEO
        title={listing.title}
        description={listing.description?.slice(0, 160)}
        image={listing.images?.[0]}
        type="product"
      />
      {listing && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": listing.listingType === 'vehicle' ? "Car" : "Product",
            "name": listing.title,
            "description": listing.description?.slice(0, 300),
            "image": listing.images?.[0] || '',
            ...(listing.listingType === 'vehicle' ? {
              "vehicleModelDate": (listing as any).year?.toString(),
              "manufacturer": { "@type": "Organization", "name": (listing as any).make },
              "model": (listing as any).model,
              "mileageFromOdometer": (listing as any).odometer ? {
                "@type": "QuantitativeValue",
                "value": (listing as any).odometer,
                "unitCode": "KMT"
              } : undefined,
              "bodyType": (listing as any).bodyType,
            } : {}),
            ...(listing.price != null && listing.price > 0 ? {
              "offers": {
                "@type": "Offer",
                "price": listing.price,
                "priceCurrency": "NZD",
                "availability": listing.status === 'active' ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
                "itemCondition": listing.condition === 'new' ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition"
              }
            } : {})
          })}</script>
        </Helmet>
      )}

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="container py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-700">
              Home
            </Link>
            <span className="text-slate-300">/</span>
            <Link to="/browse" className="text-slate-500 hover:text-slate-700">
              Browse
            </Link>
            {category && (
              <>
                <span className="text-slate-300">/</span>
                <Link
                  to={`/browse?category=${category.id}`}
                  className="text-slate-500 hover:text-slate-700"
                >
                  {category.name}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="container py-4 md:py-6">
        <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
          {/* Image Gallery */}
          <div className="min-w-0">
            <div className="relative aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden mb-4">
              {listing.images?.[activeImageIndex] && !failedImages.has(activeImageIndex) ? (
                <img
                  src={listing.images[activeImageIndex]}
                  alt={listing.title}
                  className="w-full h-full object-cover cursor-pointer"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onClick={() => setLightboxOpen(true)}
                  onError={() => setFailedImages((prev) => new Set(prev).add(activeImageIndex))}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <span className="text-6xl" aria-label="No image available" role="img">📷</span>
                </div>
              )}

              {/* Navigation arrows */}
              {listing.images && listing.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {isAuction && (
                  <span className="badge bg-purple-500 text-white">Auction</span>
                )}
              </div>

              {/* Click-to-enlarge hint — images are cropped to fill; the full
                  picture opens in the lightbox on click (client report lSkOFdGp). */}
              {listing.images?.[activeImageIndex] && !failedImages.has(activeImageIndex) && (
                <button
                  onClick={() => setLightboxOpen(true)}
                  className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/60 text-white text-xs font-medium px-2.5 py-1.5 rounded-full hover:bg-black/75 transition-colors"
                  aria-label="View full image"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">View full image</span>
                </button>
              )}

              {/* Image counter */}
              {listing.images && listing.images.length > 1 && (
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full">
                  {activeImageIndex + 1} / {listing.images.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {listing.images && listing.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 scroll-smooth">
                {listing.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={clsx(
                      'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors',
                      activeImageIndex === index
                        ? 'border-primary-500'
                        : 'border-transparent hover:border-slate-300'
                    )}
                  >
                    {!failedImages.has(index) ? (
                      <img
                        src={image}
                        alt={`${listing.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={() => setFailedImages((prev) => new Set(prev).add(index))}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400 text-sm" aria-label="Image unavailable" role="img">📷</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Business Listing Banner (desktop — between images and description) */}
            {sellerProfile && sellerProfile.subscriptionTier !== 'general' && sellerProfile.listingBannerUrl && (
              <div className="hidden lg:block mt-4 mb-2">
                <img
                  src={sellerProfile.listingBannerUrl}
                  alt={`${sellerProfile.displayName || 'Seller'} banner`}
                  className="w-full rounded-xl object-contain max-h-48"
                />
              </div>
            )}

            {/* Description — below images on left column (desktop) */}
            <div className="hidden lg:block mt-6">
              <div className="card p-6 mb-6">
                <h2 className="text-lg font-semibold mb-3">Description</h2>
                <div className="prose prose-slate max-w-none">
                  <p className="whitespace-pre-wrap text-slate-600 leading-relaxed break-words">
                    {listing.description}
                  </p>
                </div>

                {/* Condition */}
                {listing.condition && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500">Condition:</span>
                      <span className="badge-primary capitalize">{listing.condition}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Vehicle Details (desktop) */}
              {isVehicleListing(listing) && (
                <VehicleDetails listing={listing as VehicleListing} />
              )}

              {/* Fundraiser Section (desktop) */}
              {isFundraiserListing(listing) && (
                <FundraiserSection listing={listing as FundraiserListing} />
              )}

            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* RIGHT COLUMN                                               */}
          {/* Mobile: flex-col + order classes reorder sections           */}
          {/* Desktop: lg:order-none resets to source order               */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="min-w-0 flex flex-col">

            {/* ── 1. Business Listing Banner (mobile only — desktop banner is in left column) ── */}
            {sellerProfile && sellerProfile.subscriptionTier !== 'general' && sellerProfile.listingBannerUrl && (
              <div className="order-1 lg:hidden mb-4">
                <img
                  src={sellerProfile.listingBannerUrl}
                  alt={`${sellerProfile.displayName || 'Seller'} banner`}
                  className="w-full rounded-xl object-contain max-h-32"
                />
              </div>
            )}

            {/* ── 2. Price, Title, Meta ── */}
            <div className="order-2 lg:order-[1]">
              {/* Price */}
              <div className="mb-4">
                {isAuction ? (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Current Bid</p>
                    <p className="text-2xl md:text-3xl font-bold text-slate-900">
                      {formatPrice(
                        (listing as Listing & { currentBid?: number }).currentBid ||
                          listing.price
                      )}
                    </p>
                    {(listing as Listing & { bidCount?: number }).bidCount ? (
                      <p className="text-sm text-slate-500">
                        {(listing as Listing & { bidCount?: number }).bidCount} bids
                      </p>
                    ) : (
                      <p className="text-sm text-slate-500">No bids yet</p>
                    )}
                    {listing.expiresAt && (
                      <div className="flex items-center gap-1 text-sm text-purple-600 mt-2">
                        <Clock className="w-4 h-4" />
                        {formatTimeRemaining(listing.expiresAt)}
                      </div>
                    )}
                  </div>
                ) : isFundraiserListing(listing) ? (
                  <div>
                    <p className="text-2xl md:text-3xl font-bold text-primary-600">
                      Fundraiser
                    </p>
                    {(listing as FundraiserListing).organizationName && (
                      <p className="text-sm text-slate-500 mt-1">
                        by {(listing as FundraiserListing).organizationName}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">
                    {formatPrice(listing.price)}
                  </p>
                )}
              </div>

              {/* Title */}
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 break-words">
                {listing.title}
              </h1>

              {/* Verified nonprofit badge */}
              {sellerProfile?.subscriptionTier === 'nonprofit' && sellerProfile?.nonprofitVerified && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-4 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium">
                  <ShieldCheck className="w-4 h-4" />
                  Verified Nonprofit
                </div>
              )}

              {/* Seller email (general accounts with showEmailOnListings) */}
              {sellerProfile?.showEmailOnListings && sellerProfile.email && (
                <div className="flex items-center gap-1 text-sm text-slate-500 mb-4">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${sellerProfile.email}`} className="hover:text-primary-600">{sellerProfile.email}</a>
                </div>
              )}
            </div>

            {/* ── 3. Mobile-only: Description, Condition, Vehicle/Fundraiser details ── */}
            <div className="order-3 lg:hidden">
              {/* Description */}
              <div className="card p-4 sm:p-5 mb-4">
                <h2 className="text-lg font-semibold mb-3">Description</h2>
                <p className="whitespace-pre-wrap text-slate-600 leading-relaxed break-words">
                  {listing.description}
                </p>
                {listing.condition && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500">Condition:</span>
                      <span className="badge-primary capitalize">{listing.condition}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Vehicle Details (mobile) */}
              {isVehicleListing(listing) && (
                <VehicleDetails listing={listing as VehicleListing} />
              )}

              {/* Fundraiser Section (mobile) */}
              {isFundraiserListing(listing) && (
                <div className="mb-6">
                  <FundraiserSection listing={listing as FundraiserListing} />
                </div>
              )}
            </div>

            {/* ── 4. View Store (StorefrontPreviewCard) ── */}
            <div className="order-4 lg:order-[2] mb-4">
              <StorefrontPreviewCard
                sellerId={listing.sellerId}
                sellerName={listing.sellerName || 'Seller'}
                verified={listing.sellerVerified}
                rating={listing.sellerRating}
                listingType={listing.listingType}
              />
            </div>

            {/* ── 4b. Sales team (Storefront + Dealership) ── */}
            <AgentContactStack
              seller={sellerProfile}
              className="order-4 lg:order-[2] mb-4"
            />

            {/* ── 5. Contact, Bidding, Cart, Offers, Actions ── */}
            <div className="order-5 lg:order-[3]">
              {/* Auction Bidding Section */}
              {isAuction && (
                <div className="mb-4">
                  <BidSection listing={listing} />

                  {/* Buy Now option on auctions (before the first bid, Trade Me style) */}
                  {canBuyOutAuction(listing, isOwner) && (
                    <div className="mt-4 p-3 sm:p-4 bg-primary-50 border border-primary-200 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-primary-800">Buy Now</p>
                          <p className="text-lg font-bold text-primary-900">
                            {formatPrice(auctionBuyNowPrice)}
                          </p>
                          <p className="text-xs text-primary-600">Skip the auction and buy instantly</p>
                        </div>
                        {!sellerAcceptsAnyPayment ? (
                          <Button
                            variant="primary"
                            onClick={handleContact}
                            leftIcon={<MessageSquare className="w-4 h-4" />}
                            className="w-full sm:w-auto"
                          >
                            Contact Seller
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            onClick={handleAuctionBuyOut}
                            disabled={buyingOut}
                            leftIcon={buyingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                            className="w-full sm:w-auto"
                          >
                            {buyingOut ? 'Processing…' : 'Buy Now'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stock indicator (e-commerce listings) */}
              {isProduct && (
                <div className="mb-4">
                  {isOutOfStock ? (
                    <div className="flex items-center gap-2 text-red-600">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium">Out of Stock</span>
                    </div>
                  ) : lowStock ? (
                    <div className="flex items-center gap-2 text-amber-600">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium">Low stock — {listing.stock} left</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium">{listing.stock} in stock</span>
                    </div>
                  )}
                  {listing.sold != null && listing.sold > 0 && (
                    <p className="text-xs text-slate-500 mt-1">{listing.sold} sold</p>
                  )}
                </div>
              )}

              {/* Add to Cart (e-commerce mode, non-owner, active) */}
              {isProduct && !isOwner && listing.status === 'active' && (
                <div className="mb-4 space-y-3">
                  {!sellerAcceptsAnyPayment ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      This seller has not set up payment processing yet. Please contact them directly to arrange purchase.
                    </div>
                  ) : isInStock ? (
                    <>
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-slate-700">Qty:</label>
                        <select
                          value={cartQty}
                          onChange={(e) => setCartQty(Number(e.target.value))}
                          className="input w-20 !py-1.5"
                        >
                          {Array.from(
                            { length: Math.min(listing.stock ?? 1, 99) },
                            (_, i) => i + 1
                          ).map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                      <div className="hidden md:flex gap-3">
                        <Button
                          variant="primary"
                          className="flex-1"
                          leftIcon={<ShoppingCart className="w-4 h-4" />}
                          onClick={handleAddToCart}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button variant="secondary" disabled className="w-full">
                      Out of Stock
                    </Button>
                  )}
                </div>
              )}

              {/* Buy Now — any fixed-price listing (general items, vehicles, dealership-tier inventory). */}
              {hasBuyNow && isBuyNow && !isProduct && !isFundraiserListing(listing) && !isOwner && listing.status === 'active' && (
                <div className="mb-4">
                  {isSellerEnquiryOnly ? (
                    <div className="hidden md:flex gap-3">
                      <Button
                        variant="primary"
                        className="flex-1"
                        leftIcon={<MessageSquare className="w-4 h-4" />}
                        onClick={handleContact}
                      >
                        Contact Dealership
                      </Button>
                    </div>
                  ) : !sellerAcceptsAnyPayment ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      This seller has not set up payment processing yet. Please contact them directly to arrange purchase.
                    </div>
                  ) : (
                    <div className="hidden md:flex gap-3">
                      <Button
                        variant="primary"
                        className="flex-1"
                        leftIcon={<CreditCard className="w-4 h-4" />}
                        onClick={() => handleBuyNow()}
                      >
                        Buy Now
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Make Offer — gated on enableOffers toggle (with legacy fallback). */}
              {!isOwner && acceptsOffers && !isProduct && listing.status === 'active' && !isFundraiserListing(listing) && (
                <div className="mb-4">
                  <MakeOfferSection listing={listing} />
                </div>
              )}

              {/* Share only — a store product page, not a marketplace listing */}
              <div className="mb-6">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-600"
                >
                  <Share2 className="w-4 h-4" /> Share this product
                </button>
              </div>
            </div>

            {/* ── Finance Calculator (right column, above Safety Tips) ──
                Visibility is decided by the admin tier policy (settings/financeCalculator.tierPolicies)
                via resolveFinanceVisibility. Good Lending is policy-gated; the dealership own-provider
                path resolves independently. */}
            {listing.price != null && (() => {
              // Fall back to defaults (enabled:false) so the own-provider path — which doesn't
              // depend on the global doc — still resolves if the settings fetch hasn't landed.
              const fs = financeSettings ?? DEFAULT_FINANCE_SETTINGS
              const visibility = resolveFinanceVisibility({
                listing: {
                  price: listing.price,
                  financeOptIn: listing.financeOptIn,
                  categoryGroup: getCategoryById(listing.category)?.group,
                },
                seller: {
                  subscriptionTier: sellerProfile?.subscriptionTier,
                  financeSource: sellerProfile?.financeSource,
                  financeCalculatorEnabled: sellerProfile?.financeCalculatorEnabled,
                  financeProviderName: sellerProfile?.financeProviderName,
                  financeMinAmount: sellerProfile?.financeMinAmount,
                },
                settings: fs,
              })
              if (!visibility.show) return null

              if (visibility.source === 'good-lending') {
                return (
                  <div className="order-5 lg:order-[4] mb-6">
                    <FinanceCalculator
                      price={listing.price}
                      defaultRate={fs.defaultRate}
                      providerName={fs.providerName}
                      providerLogo={fs.providerLogo}
                      providerUrl={fs.providerUrl}
                      description={fs.description}
                      contactEmail={fs.contactEmail}
                      contactPhone={fs.contactPhone}
                      minAmount={fs.minAmount}
                      maxTermYears={fs.maxTermYears}
                      termsUrl={fs.termsUrl}
                    />
                  </div>
                )
              }

              // source === 'own'
              return (
                <div className="order-5 lg:order-[4] mb-6">
                  <FinanceCalculator
                    price={listing.price}
                    defaultRate={sellerProfile?.financeRate || undefined}
                    providerName={sellerProfile?.financeProviderName}
                    providerLogo={sellerProfile?.financeProviderLogo}
                    providerUrl={sellerProfile?.financeProviderUrl}
                    description={sellerProfile?.financeDescription}
                    contactEmail={sellerProfile?.financeContactEmail}
                    contactPhone={sellerProfile?.financeContactPhone}
                    minAmount={sellerProfile?.financeMinAmount}
                    maxTermYears={sellerProfile?.financeMaxTermYears}
                    termsUrl={sellerProfile?.financeTermsUrl}
                  />
                </div>
              )
            })()}

            {/* ── 6. Safety Tips ── */}
            <div className="order-6 lg:order-[5] mb-6">
              <SafetyTips />
            </div>

            {/* Reviews live on the seller's store page (/seller/:sellerId), not on
                individual listings — buyers reach them via the View Store card above. */}

            {/* ── 8. Enquiry Form (mobile: before safety tips, desktop: after actions) ── */}
            {sellerProfile?.enableEnquiryForm && sellerProfile.email && !isOwner && (
              <div className="order-[5] lg:order-[4] mb-4">
                <EnquiryForm
                  listingId={listing.id}
                  listingTitle={listing.title}
                  listingImage={listing.images?.[0]}
                  listingPrice={listing.price}
                  sellerId={listing.sellerId}
                  sellerName={listing.sellerName || 'Seller'}
                  sellerEmail={sellerProfile.email}
                />
              </div>
            )}

            {/* ── 9. Shipping, Status Controls ── */}
            <div className="order-8 lg:order-[4]">
              {/* Shipping & Payment */}
              <ShippingPaymentSection listing={listing} />

              {/* Status Controls (owner only) */}
              {isOwner && (
                <div className="mb-6">
                  <ListingStatusControls listing={listing} />
                </div>
              )}

              {/* Bid list (auction owner only) */}
              {isOwner && isAuction && (
                <div className="mb-6">
                  <SellerBidList listingId={listing.id} />
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Related Listings */}
        <div className="mt-12 border-t border-slate-200 pt-12">
          <RelatedListings
            currentListingId={listing.id}
            category={listing.category}
            sellerId={listing.sellerId}
          />
        </div>

        {/* Sponsor ad */}
        <div className="mt-8">
          <AdCarousel placement="listing" />
        </div>
      </div>

      {/* Mobile sticky buy bar */}
      <div className="fixed bottom-[60px] left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between md:hidden z-30">
        <div className="min-w-0 mr-3">
          <p className="text-lg font-bold text-slate-900 truncate">{isFundraiserListing(listing) ? 'Fundraiser' : formatPrice(listing.price)}</p>
          <p className="text-xs text-slate-500 truncate">{listing.title}</p>
        </div>
        {isProduct && !sellerAcceptsAnyPayment && !isOwner ? (
          <span className="text-xs text-amber-600 font-medium">Payments not set up</span>
        ) : isProduct && isInStock && !isOwner ? (
          <Button variant="primary" onClick={handleAddToCart} size="sm" className="flex-shrink-0">
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            Add to Cart
          </Button>
        ) : isProduct && isOutOfStock ? (
          <Button variant="secondary" disabled size="sm" className="flex-shrink-0">
            Out of Stock
          </Button>
        ) : hasBuyNow && isBuyNow && !isProduct && !isFundraiserListing(listing) && !isOwner && sellerAcceptsAnyPayment ? (
          <Button variant="primary" onClick={() => handleBuyNow()} size="sm" className="flex-shrink-0">
            <CreditCard className="w-4 h-4 mr-1.5" />
            Buy Now
          </Button>
        ) : canBuyOutAuction(listing, isOwner) && sellerAcceptsAnyPayment ? (
          <Button variant="primary" onClick={handleAuctionBuyOut} disabled={buyingOut} size="sm" className="flex-shrink-0">
            {buyingOut ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
            {buyingOut ? 'Processing…' : 'Buy Now'}
          </Button>
        ) : !isOwner && sellerProfile?.enableChat !== false ? (
          <Button variant="primary" onClick={handleContact} size="sm" className="flex-shrink-0">
            <MessageSquare className="w-4 h-4 mr-1.5" />
            Contact
          </Button>
        ) : null}
      </div>

      {/* Lightbox */}
      {lightboxOpen && listing.images && listing.images.length > 0 && (
        <ImageLightbox
          images={listing.images}
          initialIndex={activeImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  )
}

// Vehicle-specific details component
function VehicleDetails({ listing }: { listing: VehicleListing }) {
  const keySpecs = [
    { label: 'Year', value: listing.year?.toString() },
    { label: 'Odometer', value: formatOdometer(listing.odometer) },
    { label: 'Transmission', value: listing.transmission },
    { label: 'Fuel Type', value: listing.fuelType },
  ].filter((spec) => spec.value)

  const allSpecs = [
    { label: 'Make', value: listing.make },
    { label: 'Model', value: listing.model },
    { label: 'Model Detail', value: listing.modelDetail },
    { label: 'Year', value: listing.year?.toString() },
    { label: 'Odometer', value: formatOdometer(listing.odometer) },
    { label: 'Colour', value: listing.colour },
    { label: 'Body Type', value: listing.bodyType },
    { label: 'Import History', value: listing.importHistory },
    { label: 'Seats', value: listing.seats?.toString() },
    { label: 'Doors', value: listing.doors?.toString() },
    { label: 'Engine', value: listing.engineCC ? `${listing.engineCC}cc` : undefined },
    { label: 'Cylinders', value: listing.cylinders?.toString() },
    { label: 'Fuel Type', value: listing.fuelType },
    { label: 'Transmission', value: listing.transmission },
    { label: 'Previous Owners', value: listing.previousOwners?.toString?.() ?? listing.previousOwners },
    { label: 'WOF Expiry', value: listing.wofExpiry },
    { label: 'Rego Expiry', value: listing.regoExpiry },
    { label: 'Plate / VIN', value: listing.plateOrVin },
  ].filter((spec) => spec.value)

  // Show background-checks section if EITHER the legacy CarJam check fields are
  // populated OR the seller has declared any of the new boolean flags.
  const hasBackgroundChecks =
    listing.stolenCheck ||
    listing.damageCheck ||
    listing.reportedStolen !== undefined ||
    listing.importedDamaged !== undefined

  return (
    <div className="card mb-6 overflow-hidden">
      <div className="bg-slate-800 text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
        <Car className="w-5 h-5 text-primary-400" />
        <h2 className="text-base sm:text-lg font-semibold">Vehicle Details</h2>
      </div>

      {/* Key specs highlight row */}
      {keySpecs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-slate-200">
          {keySpecs.map((spec) => (
            <div key={spec.label} className="px-3 sm:px-4 py-3 text-center border-b sm:border-b-0 sm:border-r last:border-r-0 last:border-b-0 border-slate-200">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{spec.label}</p>
              <p className="text-sm sm:text-base font-semibold text-slate-900 mt-0.5">{spec.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Full specs grid */}
      {allSpecs.length > 0 && (
        <div className="divide-y divide-slate-100">
          {allSpecs.map((spec) => (
            <div key={spec.label} className="flex items-center justify-between px-4 sm:px-6 py-2.5">
              <span className="text-sm text-slate-500">{spec.label}</span>
              <span className="text-sm font-medium text-slate-900 text-right">{spec.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Background Checks — declared by seller, optionally pre-filled by CarJam */}
      {hasBackgroundChecks && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 sm:px-6 py-3 sm:py-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
            <ShieldCheck className="w-5 h-5 text-primary-500" />
            Background Checks
          </h3>
          <div className="space-y-2">
            {/* Prefer the new boolean field; fall back to legacy Passed/Failed/Pending */}
            {(listing.reportedStolen !== undefined || listing.stolenCheck) && (
              <BackgroundCheckRow
                label="Reported Stolen"
                clean={listing.reportedStolen !== undefined
                  ? !listing.reportedStolen
                  : listing.stolenCheck === 'Passed'}
                displayValue={listing.reportedStolen !== undefined
                  ? (listing.reportedStolen ? 'Yes — reported stolen' : 'No')
                  : listing.stolenCheck!}
              />
            )}
            {(listing.importedDamaged !== undefined || listing.damageCheck) && (
              <BackgroundCheckRow
                label="Imported Damaged"
                clean={listing.importedDamaged !== undefined
                  ? !listing.importedDamaged
                  : listing.damageCheck === 'Passed'}
                displayValue={listing.importedDamaged !== undefined
                  ? (listing.importedDamaged ? 'Yes — imported damaged' : 'No')
                  : listing.damageCheck!}
              />
            )}
          </div>
        </div>
      )}

      {/* On-road costs declaration */}
      {listing.onRoadCostsIncluded !== undefined && (
        <div className="border-t border-slate-200 px-4 sm:px-6 py-3 sm:py-4 bg-white">
          <div className="flex items-start gap-3">
            {listing.onRoadCostsIncluded ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className="text-sm font-medium text-slate-900">
                {listing.onRoadCostsIncluded ? 'On-road costs included' : 'On-road costs not included'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {listing.onRoadCostsIncluded
                  ? 'Sold with current WoF, registration, and Road User Charges.'
                  : 'Buyer may need to pay additional costs to legally drive the car on NZ roads.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Small helper for a uniform Background Check row — green when clean, red otherwise.
function BackgroundCheckRow({ label, clean, displayValue }: { label: string; clean: boolean; displayValue: string }) {
  return (
    <div className="flex items-center justify-between bg-white rounded-lg px-3 sm:px-4 py-2.5 border border-slate-200">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={clsx(
        'flex items-center gap-1.5 text-sm font-medium',
        clean ? 'text-green-600' : 'text-red-600',
      )}>
        {clean ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {displayValue}
      </span>
    </div>
  )
}

// Shipping & Payment section
function ShippingPaymentSection({ listing }: { listing: Listing }) {
  // Vehicles are pickup/inspection only — no courier/post shipping (bug p0TsXWA2)
  const isVehicle = listing.listingType === 'vehicle'
  const shippingOptions = isVehicle
    ? undefined
    : (listing as Listing & { shippingOptions?: string[] }).shippingOptions
  const paymentOptions = (listing as Listing & { paymentOptions?: string[] }).paymentOptions

  if (!shippingOptions?.length && !paymentOptions?.length) return null

  return (
    <div className="mb-6 space-y-4">
      {shippingOptions && shippingOptions.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-slate-500" />
            Shipping & Delivery
          </h3>
          <div className="flex flex-wrap gap-2">
            {shippingOptions.map((option) => (
              <span key={option} className="badge bg-slate-100 text-slate-700">
                {option}
              </span>
            ))}
          </div>
        </div>
      )}

      {paymentOptions && paymentOptions.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-500" />
            Payment Options
          </h3>
          <div className="flex flex-wrap gap-2">
            {paymentOptions.map((option) => (
              <span key={option} className="badge bg-slate-100 text-slate-700">
                {option}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
